// @flow

import _ from 'lodash';
import ko from 'rgs-knockout';
import { compactJoin } from 'common-utils';
import BaseViewModel from 'rgs-nfd-ui-kit/ko-components/tree/BaseViewModel';
import { Address as AddressKladr } from 'app-my-account/models/address.js';
import type {
	HouseModel,
	IKladrService,
	RegionModel, SettlementDetailModel,
	SettlementShortModel, SettlementShortWithTitleModel, StreetDetailModel, StreetShortModel,
} from 'app-my-account/services/kladr/api-models/kladr.flow';
import ObservableMap from 'observable-map';
import stringify from 'json-stable-stringify';
import type { Observable } from 'rgs-knockout';
import { ObservableBusy } from 'observable-busy';
import type { CheckboxArguments } from 'rgs-nfd-ui-kit/ko-components/checkbox/checkbox';
import type { AddressEditorOptions } from 'rgs-nfd-ui-kit/ko-components/address-editor/flow';
import { StrategySettlements } from './strategy-settlements';
import type { TItemsResponse } from 'app/rgs/api/request-result';
import { StrategyStreets } from './strategy-streets';
import { StrategyHouses } from 'rgs-nfd-ui-kit/ko-components/address-editor/strategy-houses';
import type { ObservableBusyInterface } from 'observable-busy';

export class AddressEditorViewModel extends BaseViewModel<AddressEditorOptions> {

	strategySettlements: StrategySettlements;
	strategyStreets: StrategyStreets;
	strategyHouses: StrategyHouses;

	busy: ObservableBusyInterface = new ObservableBusy();

	dictionaryRegions: ObservableMap = new ObservableMap({ hash: (v) => stringify(v) });

	dontHasStreetsCheckbox: CheckboxArguments;
	address: AddressKladr = new AddressKladr();

	isInitFinished: Observable<boolean>;
	_options: AddressEditorOptions;

	get kladrService (): IKladrService {
		return this._options.kladrService;
	}

	constructor (options: AddressEditorOptions) {
		super(options);

		this._options = _.defaults(options, {
			settlementVisible: ko.observable(true),
			streetVisible: ko.observable(true),
			houseVisible: ko.observable(true),
			apartmentVisible: ko.observable(true),
			isSettlementDisabled: false,
			isStreetDisabled: false,
			isHouseDisabled: false,
			isAppartmentDisabled: false,
		});

		this.address.Settlement.Kladr = (this._options.settlement.kladr
			|| this._options.settlement.Kladr);
		this.address.Settlement.Name = (this._options.settlement.name
			|| this._options.settlement.Name);

		this.address.Street.Kladr = this._options.street.kladr || this._options.street.Kladr;
		this.address.Street.Name = this._options.street.name || this._options.street.Name;

		this.address.House.Kladr = this._options.house.kladr || this._options.house.Kladr;
		this.address.House.Name = this._options.house.name || this._options.house.Name;

		this.address.Apartment = this._options.apartment;

		this.address.HasStreets = this._options.hasStreets;

		this.hasStreets = ko.pureComputed(() => {
			return this.address.HasStreets() || this.address.Settlement.HasStreets();
		});

		this.hasHouses = ko.pureComputed(() => {
			if (!this.address.HasStreets()) {
				return true;
			}

			return Boolean(this.address.Settlement.HasHouses());
		});

		this.dontHasStreets = ko.observable(!this.address.HasStreets());
		this.dontHasStreets.subscribe((newVal: boolean) => {
			this.address.HasStreets(!newVal);
		});

		this.address.Settlement.Kladr.subscribe(() => {
			this.dontHasStreets(!this.address.Settlement.HasStreets());
		});

		this.dontHasStreetsCheckbox = {
			content: 'Город без улиц',
			checked: this.dontHasStreets,
			onChange: ({ isChecked }) => {
				this.address.HasStreets(!isChecked);
				this.strategyStreets.onClickCancel();
				this.strategyStreets.clear();
				this.strategyHouses.onClickCancel();
				this.strategyHouses.clear();
			},
			isDisabled: this._options.isSettlementDisabled,
		};

		this.strategySettlements = new StrategySettlements({
			fetchSearch: _.throttle(this._fetchSettlements, 400),
			onClickItem: this._onChangeSettlement,
			onClickCancel: async () => {
				this.address.Settlement.clear();
				this.strategyStreets.onClickCancel();
				this.strategyStreets.clear();
				this.strategyHouses.onClickCancel();
				this.strategyHouses.clear();
			},
			isDisabled: ko.pureComputed(() => {
				return this._options.isSettlementDisabled;
			}),
		});

		this.strategyStreets = new StrategyStreets({
			fetchSearch: this._fetchStreets,
			onClickItem: this._onChangeStreet,
			onClickCancel: async () => {
				this.address.Street.clear();
				this.strategyHouses.onClickCancel();
				this.strategyHouses.clear();
			},
			isDisabled: ko.pureComputed(() => {
				let isDisabled = !this.hasStreets();

				if (!isDisabled) {
					const settlementId = this.strategySettlements.getCurrentId();
					isDisabled = (_.toString(settlementId)).length === 0;
				}

				return isDisabled
					|| this._options.isStreetDisabled
					|| !this.strategySettlements.getCurrentId()
					|| !this.address.HasStreets();
			}),
		});

		this.strategyHouses = new StrategyHouses({
			fetchSearch: this._fetchHouses,
			onClickItem: this._onChangeHouse,
			onClickCancel: async () => {
				this.address.House.clear();
			},
			isDisabled: ko.pureComputed(() => {
				let isDisabled = true;

				if (!this.address.Settlement.Kladr()) {
					isDisabled = true;
				} else if (!this.address.HasStreets()) {
					isDisabled = false;
				} else {
					const cityId = this.strategyStreets.getCurrentId();
					isDisabled = (_.toString(cityId)).length === 0;
				}

				return isDisabled
					|| this._options.isHouseDisabled
					|| !this.strategyStreets.getCurrentId();
			}),
		});

		this.busy.add(this._init()).then(() => this.isInitFinished(true));
	}

	_init = async () => {
		// для продолжения работы подгружаем Regions
		await this._loadRegions();
		const settlementKladr = this.address.Settlement.Kladr();
		const streetKladr = this.address.Street.Kladr();
		const houseKladr = this.address.House.Kladr();
		const houseName = this.address.House.Name();

		this.dontHasStreets(!this.address.HasStreets());

		if (!_.isNil(settlementKladr)) {
			const settlement = await this._fetchSettlement(settlementKladr);
			this.address.Settlement.fromJS(settlement);
			this.strategySettlements.setCurrentSettlement(settlement);
			this.strategySettlements.inputValue(_.trim(_.trim(settlement.Name, 'гсд')));
			await this.strategySettlements.refresh();
			this.dontHasStreets(!settlement.HasStreets);
		}

		if (!_.isNil(streetKladr)) {
			const street: StreetDetailModel = await this._fetchStreet(streetKladr);
			this.address.Street.fromJS(street);
			this.strategyStreets.setCurrentStreet(street);
			this.strategyStreets.inputValue(street.Name);
			await this.strategyStreets.refresh();
		}

		if (!_.isNil(houseKladr) || !_.isNil(houseName)) {
			const house = {
				Kladr: houseKladr,
				Name: houseName,
			};
			this.strategyHouses.setCurrentHouse(house);
			this.strategyHouses.inputValue(house.Name);
			this.address.House.fromJS(house);
			await this.strategyHouses.refresh();
		}
	};

	_loadRegions = async () => {
		const { Data: regions } = await this.kladrService.fetchRegions();

		_.each(regions, region => this.dictionaryRegions.setAbsent(region.ID, region));

		// TODO: заполнить имеющимися значениями выпадающие списки
	};

	_onChangeSettlement = async (kladr: string) => {
		const { Data: settlementDetail } = await this.kladrService.fetchSettlement(kladr);
		this.address.Settlement.fromJS(settlementDetail);
		await this.strategyStreets.onClickCancel();
		await this.strategyStreets.clear();
		await this.strategyHouses.onClickCancel();
		await this.strategyHouses.clear();
	};

	_onChangeStreet = async (street: StreetShortModel) => {
		this.address.Street.fromJS(street);
		this.strategyHouses.onClickCancel();
		this.strategyHouses.clear();
	};

	_onChangeHouse = (house: HouseModel) => {
		this.address.House.fromJS(house);
	};

	_fetchStreets = async (
		params: { search: string },
	): Promise<TItemsResponse<StreetShortModel>> => {
		if (_.isNil(params) || _.isNil(params.search)) {
			return {
				Items: [],
				Total: 0,
			};
		}

		const { Data: streets } = await this.kladrService.fetchStreets({
			search: params.search,
			settlementKladr: ko.unwrap(this.address.Settlement.Kladr),
		});

		return {
			Items: streets,
			Total: streets.length,
		};
	};

	_fetchStreet = async (streetKladr: string): StreetDetailModel => {
		const { Data: street } = await this.kladrService.fetchStreet(streetKladr);
		return street;
	};

	_fetchHouses = async (
		params: { search: string },
	): Promise<TItemsResponse<HouseModel>> => {
		if (_.isNil(params) || _.isNil(params.search)) {
			return {
				items: [],
				total: 0,
			};
		}

		const { Data: houses } = await this.kladrService.fetchHouses({
			search: params.search,
			settlementKladr: ko.unwrap(this.address.Settlement.Kladr),
			streetKladr: ko.unwrap(this.address.Street.Kladr),
		});

		return {
			Items: houses,
			Total: houses.length,
		};
	};

	_genSettlementTitle = (settlement: SettlementShortModel): SettlementShortWithTitleModel => {
		const region: RegionModel = this.dictionaryRegions.get(settlement.RegionID);
		return {
			...settlement,
			Title: compactJoin(
				settlement.Name,
				settlement.CityName,
				settlement.AreaName,
				region && region.Name,
			),
		};
	};

	_fetchSettlement = async (settlementKladr: string): SettlementDetailModel => {
		const { Data: settlement } = await this.kladrService.fetchSettlement(settlementKladr);
		return settlement;
	};

	_fetchSettlements = async (
		params: { search: string },
	): Promise<TItemsResponse<SettlementShortWithTitleModel>> => {
		if (_.isNil(params) || _.isNil(params.search)) {
			return {
				Items: [],
				Total: 0,
			};
		}

		const { Data: settlements } = await this.kladrService.fetchSettlements({
			search: params.search,
		});
		const prepareSettlements: [] = _.map(settlements, (settlement: SettlementShortModel) => {
			return this._genSettlementTitle(settlement);
		});

		return {
			Items: prepareSettlements,
			Total: prepareSettlements.length,
		};
	};

}
